<script>
import appConfig from '@src/app.config'

export default {
  page: {
    // All subcomponent titles will be injected into this template.
    titleTemplate(title) {
      title = typeof title === 'function' ? title(this.$store) : title
      return title ? `${title} | ${appConfig.title}` : appConfig.title
    },
  },
}
</script>

<template>
  <div id="app">
    <!--
    Even when routes use the same component, treat them
    as distinct and create the component again.
    -->
    <RouterView :key="$route.fullPath" />
  </div>
</template>

<!-- This should generally be the only global CSS in the app. -->
<style lang="scss">
// Allow element/type selectors, because this is global CSS.
// stylelint-disable selector-max-type, selector-class-pattern

// Normalize default styles across browsers,
// https://necolas.github.io/normalize.css/
@import '~normalize.css/normalize.css';
// Style loading bar between pages.
// https://github.com/rstacruz/nprogress
@import '~nprogress/nprogress.css';

@import '~@riophae/vue-treeselect/dist/vue-treeselect.css';

@tailwind base;
@tailwind components;

@font-face {
  font-family: 'ProximaNova';
  font-weight: 400;
  src: url('~@assets/fonts/proxima-nova/Proxima Nova Regular.ttf')
    format('truetype');
}
@font-face {
  font-family: 'ProximaNova';
  font-weight: 100;
  src: url('~@assets/fonts/proxima-nova/Proxima Nova Light.otf')
    format('opentype');
}
@font-face {
  font-family: 'BrandonGrotesque';
  font-weight: 400;
  src: url('~@assets/fonts/brandon-grotesque/brandon-grotesque-black.otf')
    format('opentype');
}
@font-face {
  font-family: 'BrandonGrotesque';
  font-style: italic;
  font-weight: 400;
  src: url('~@assets/fonts/brandon-grotesque/brandon-grotesque-medium-italic.otf')
    format('opentype');
}
body {
  @apply font-proxima bg-blue-lightest;
}
.button,
.button:focus {
  @apply appearance-none outline-none;
}
.checkbox,
.agreement-control[type='checkbox'] {
  @apply appearance-none cursor-pointer inline-block select-none h-5 w-5 border border-solid border-blue-default bg-white outline-none rounded-checkbox align-middle;
}
.checkbox:checked,
.agreement-control[type='checkbox']:checked {
  background-color: 'blue';
  background-image: url("data:image/svg+xml,<svg viewBox='0 0 16 16' fill='%23069BD7' xmlns='http://www.w3.org/2000/svg'><path d='M5.707 7.293a1 1 0 0 0-1.414 1.414l2 2a1 1 0 0 0 1.414 0l4-4a1 1 0 0 0-1.414-1.414L7 8.586 5.707 7.293z'/></svg>");
}
.radio {
  @apply appearance-none cursor-pointer inline-block select-none h-3.5 w-3.5 border border-solid border-gray bg-white outline-none rounded-full align-middle;
}
.radio-checked {
  @apply absolute;
}
.radio:checked + .radio-checked {
  @apply bg-blue-default rounded-full h-2.5 w-2.5 border border-solid border-gray top-0 left-0 ml-0.5 mt-1.5;
}
.select {
  background-color: #fff;
  background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 140 140' width='14' height='14' xmlns='http://www.w3.org/2000/svg'><g><path d='m121.3,34.6c-1.6-1.6-4.2-1.6-5.8,0l-51,51.1-51.1-51.1c-1.6-1.6-4.2-1.6-5.8,0-1.6,1.6-1.6,4.2 0,5.8l53.9,53.9c0.8,0.8 1.8,1.2 2.9,1.2 1,0 2.1-0.4 2.9-1.2l53.9-53.9c1.7-1.6 1.7-4.2 0.1-5.8z' fill='%23069BD7'/></g></svg>");
  background-repeat: no-repeat;
  background-position: right 12px top 50%;
  @apply appearance-none shadow-input border-gray-transparent border-solid border text-blue-darker text-basel font-hairline rounded-input leading-loose px-5 outline-none w-full bg-white-transparent;
}
.rendered-form select.form-control {
  @apply select py-0;
}
.input,
.rendered-form input.form-control,
.vue-tags-input .ti-input {
  @apply appearance-none shadow-input border-gray-transparent border-solid border text-blue-darker text-basel font-hairline rounded-input leading-loose px-5 outline-none w-full bg-white-transparent;
}
.vue-tags-input.border-volunteer_red .ti-input {
  @apply border-volunteer_red;
}
::placeholder {
  @apply opacity-100 text-gray-light font-hairline;
}
.home-headline1 {
  @apply text-blue-darker text-35xl tracking-normal leading-normal italic uppercase font-brandon mt-8;
}
.home-headline2 {
  @apply text-gray text-3xl mt-5 leading-tight;
}
.primary-button {
  @apply bg-blue text-white rounded-button text-lg leading-xl px-6 outline-none;

  &:disabled {
    @apply text-gray bg-gray;
  }
}
.secondary-button {
  @apply text-sm text-blue px-0;
}
.hub-selector .hub-item {
  @apply w-48 border-r border-gray-light py-3 flex flex-col justify-center items-center;
}
.hub-selector .hub-item:last-child {
  @apply border-0;
}
.hub-selector .hub-item > .img-box {
  @apply border-8 border-white rounded-full;
}
.hub-selector .hub-item > .img-box > div {
  @apply w-28 h-28 border border-gray-dark rounded-full overflow-hidden flex items-center justify-center;
}
.hub-selector .hub-item > .img-box > div > img {
  @apply w-full;
}
.hub-selector .hub-item-cur > .img-box {
  @apply border-blue-lighter border-opacity-75;
}
.hub-selector .hub-item-cur > .img-box > div {
  @apply border-blue-dark;
}
.topbar-item {
  @apply inline-block align-middle leading-2xl;
}
.topbar-item a {
  @apply text-xl text-blue;
}
.breadcrumb-separator {
  @apply mx-2;
}
.leftMenuBox {
  @apply w-26 bg-white border border-gray float-left fixed z-navbar inset-y-0 overflow-hidden;
}
#leftMenu > ul > li > div {
  @apply w-70 block overflow-hidden align-middle leading-xxl;
}
#leftMenu > ul > li ul {
  @apply bg-white mr-px;
}
#leftMenu > ul > li li {
  @apply block overflow-hidden align-middle leading-relax;
}
#leftMenu > ul > li > div > span,
#leftMenu > ul > li > div > a {
  @apply inline-block align-middle;
}
#leftMenu > ul > li > div > a:first-child {
  @apply px-9 w-26;
}
#leftMenu > ul > li > div > span > a {
  @apply inline-block align-middle;
}
#leftMenu > ul > li > div > span > a:first-child {
  @apply text-gray-light text-lg w-56 inline-block;
}
#leftMenu > ul > li > ul a:nth-child(2) {
  @apply text-gray-light text-base inline-block cursor-pointer;
}
#leftMenu li > div:hover,
#leftMenu li > ul li:hover {
  @apply bg-blue-lighter cursor-pointer;
}
#leftMenu li > div:hover a,
#leftMenu li > ul li:hover a {
  @apply text-blue;
}
#leftMenu li > ul li a.router-link-active {
  @apply text-blue;
}
#typePicker:hover + .typePicker-content {
  @apply block;
}
.typePicker-content {
  @apply hidden w-full;
}
.typePicker-content a {
  @apply block leading-looser border-l-4 -ml-0125 capitalize;
}
.typePicker-content a:hover {
  @apply cursor-pointer;
}
.typePicker-content a span {
  @apply block w-auto border-t-2 border-gray-light mx-4 text-basel text-black;
}

.typePicker-content a:hover span {
  @apply text-blue;
}
.post-container {
}
.post-container:first-child {
  @apply pt-5;
}
.post-container > div:first-child {
  @apply min-h-13 border-l-5 flex justify-between;
}
.post-details {
  @apply inline-block align-middle;
}
.post-author-name {
  @apply text-blue-darker text-3xl mr-3.5;
}
.post-date {
  @apply text-gray-light font-hairline;
}
.post-type {
  @apply text-sm italic font-brandon;
}
.post-avatar {
  @apply mr-3.5 rounded-full h-13 w-13 inline-block align-top;
}
.post-content {
  @apply ml-5 mt-5 mr-9 text-black text-basel leading-relaxed;
}
.post-causes-tags {
  @apply ml-5 mt-2 mr-9 text-gray-light text-sml inline-block leading-3;
}
.post-causes-tags > span {
  @apply pr-1 inline-block leading-3;
}
.post-causes-tags > span + span {
  @apply pl-1 border-l border-gray-darker;
}
.post-meta {
  @apply ml-5 mt-5 mr-9 pb-6 border-b border-gray;
}
.post-meta > a {
  @apply text-blue align-middle;
}
.post-meta > a + a {
  @apply mr-6.5;
}
.comments-container {
  background-color: transparent;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='10' viewBox='0 0 20 10'><g id='Group_44' data-name='Group 44' transform='translate(-626.537 -449.516)'><path id='Path_42' data-name='Path 42' d='M-1277.316,458.516l-10.147,10.147h10.147Z' transform='translate(1914 -9)' fill='%23d6f3ff'/><path id='Path_43' data-name='Path 43' d='M-1287.463,458.516l10.147,10.147h-10.147Z' transform='translate(1924 -9)' fill='%23d6f3ff'/></g></svg>");
  background-repeat: no-repeat;
  background-position: left 5rem top 0;
  @apply pt-2 mt-2;
}
.comment {
  @apply rounded-comments bg-white p-3 mb-1;
}
.comment-avatar {
  @apply mr-3.5 rounded-full h-10.5 w-10.5 inline-block;
}
.comment-author-name {
  @apply text-blue-darker text-xl mr-3.5;
}
.comment-details {
  @apply inline-block align-middle leading-xl;
}
.comment-date {
  @apply text-gray-light text-base font-hairline mr-5 align-middle;
}
.comment-content {
  @apply my-4 ml-15 text-basel text-black;
}
.comment-form {
  @apply rounded-comments bg-gray-lighter p-3;
}
.comment-form textarea {
  @apply font-proxima font-hairline text-black outline-none w-full;
}
.comment-form textarea::placeholder {
  @apply text-black;
}
.posts-bottom {
  @apply pb-6;
}
.linked-posts .posts-bottom {
  @apply pb-8;
}
.week-day-names {
  @apply border-b border-gray text-gray-light flex justify-between py-5 mb-5;
}
.week-days {
  @apply text-xl text-center flex justify-between pt-1 pb-2;
}
.week-days .other-month {
  @apply text-gray-light text-center w-6 relative;
}
.week-days .current-month {
  @apply text-blue text-center w-6 relative;
}
.week-days .has-events .bullet {
  @apply rounded bg-volunteer_red w-1 h-1 inline-block absolute mx-auto inset-x-0 bottom-0;
}
.events-calendar {
  @apply bg-white rounded-l-xl w-4/5 px-6 py-5;
}
.events-calendar .week-days {
  @apply flex justify-between pt-4 pb-2 px-4 text-3xl leading-loose;
}
.filter-dropdown {
  @apply select align-middle h-sm inline-block bg-white text-basel text-black cursor-pointer border-2 border-gray font-normal shadow-none w-34;
}
.events-tab {
  @apply text-3xl text-blue-darker px-2 border-b-4 border-transparent text-center mr-8 cursor-pointer;
}
.events-tab.active {
  @apply border-blue-default;
}
.select.inverted {
  background-image: url("data:image/svg+xml;utf8,<svg viewBox='0 0 140 140' width='14' height='14' xmlns='http://www.w3.org/2000/svg'><g><path d='m121.3,34.6c-1.6-1.6-4.2-1.6-5.8,0l-51,51.1-51.1-51.1c-1.6-1.6-4.2-1.6-5.8,0-1.6,1.6-1.6,4.2 0,5.8l53.9,53.9c0.8,0.8 1.8,1.2 2.9,1.2 1,0 2.1-0.4 2.9-1.2l53.9-53.9c1.7-1.6 1.7-4.2 0.1-5.8z' fill='%23FFFFFF'/></g></svg>");
  @apply border-0 bg-blue-default text-white;
}
.product-box-wrapper {
  @apply w-1/3 px-5.5 flex-shrink;
}
.product-box {
  @apply w-full bg-white rounded-xl shadow-box overflow-hidden mb-4.5;
}
.product-box .product-image {
  @apply h-58 overflow-hidden;
}
.product-box .product-title {
  @apply mr-7.7 h-12 text-xl text-blue;
}
.product-box .product-meta {
  @apply mx-4.5 mb-6 text-blue-darker text-base;
}
.product-details {
  @apply w-product-details;
}
.product-edit {
  @apply w-post-form;
}
.event-box {
  @apply w-full bg-white rounded-xl shadow-box overflow-hidden mb-4.5;
}
.events-grid {
  @apply flex-wrap;
}
.event-box-wrapper {
  @apply w-1/3 px-11 flex-shrink;
}
.events-grid .event-box {
  @apply bg-white rounded-xl shadow-box overflow-hidden mb-7.5;
}

.events-grid .event-box > div:last-child {
  @apply text-volunteer_red mb-4.5 mx-4.5;
}
.event-box .event-image {
  @apply h-35 overflow-hidden;
}
.event-box .event-title {
  @apply text-xl text-blue h-event-title;

  line-height: 1;
}
.event-box .event-meta {
  @apply mx-4.5 mb-3 flex justify-between;
}
.event-box .event-meta .event-date {
  @apply text-blue-darker mr-2;
}
.event-box .event-meta .event-time {
  @apply text-gray-light;
}

.events-calendar .week-days > span,
.events-calendar .week-days > button {
  @apply w-19;
}
.real-capitalize {
  @apply lowercase;
}
.real-capitalize::first-letter {
  @apply uppercase;
}
@tailwind utilities;

.vue-tags-input.input {
  padding: 0 !important;
}

.vue-tags-input .ti-new-tag-input-wrapper {
  padding: 0 !important;
  margin: 0 !important;
  font-size: 1.0625rem !important;
}
.vue-tags-input .ti-input {
  position: relative;
  z-index: 20;
  padding: 0 !important;
  @apply border border-gray-light;
}

.vue-tags-input .ti-new-tag-input-wrapper input {
  padding-right: 1.25rem !important;
  padding-left: 1.25rem !important;
  line-height: 2 !important;
  border-radius: 1.25rem;
}

.vue-tags-input .ti-selected-item {
  background-color: #069bd7 !important;
}

.vue-tags-input .ti-tag {
  background-color: #069bd7 !important;
  border-radius: 1.25rem !important;
}
.vue-tags-input .ti-item > div {
  padding: 0.25rem 1.25rem !important;
  font-size: 0.875rem;
}

.vue-tags-input .ti-autocomplete {
  position: relative !important;
  z-index: 10 !important;
  max-height: 115px;
  margin: 10px 0;
  overflow-x: auto;
  border: none !important;
}

.tooltip {
  z-index: 10000;
  display: block !important;

  .tooltip-inner {
    padding: 5px 10px 4px;
    color: #00638a;
    background: #fff;
    border: #069bd7 1px solid;
    border-radius: 12px;
  }

  .tooltip-arrow,
  .popover-arrow {
    position: absolute;
    z-index: 1;
    width: 0;
    height: 0;
    margin: 15px;
    border-color: #069bd7;
    border-style: solid;
  }

  .popover-arrow::before,
  .popover-arrow::after {
    position: absolute;
    content: '';
    border-style: solid;
  }

  .popover-arrow::after {
    border-color: #fff transparent;
  }
  .popover-arrow::before {
    border-color: #069bd7 transparent;
  }

  &[x-placement^='top'] {
    margin-bottom: 25px;

    .tooltip-arrow {
      bottom: -5px;
      left: calc(50% - 30px) !important;
      margin-top: 0;
      margin-bottom: 0;
    }
    .popover-arrow::after {
      top: -7px;
      left: 0;
      border-width: 0 15px 14px;
      transform: rotate(180deg);
    }
    .popover-arrow::before {
      top: -6px;
      left: 0;
      border-width: 0 15px 14px;
      transform: rotate(180deg);
    }
  }

  &[x-placement^='bottom'] {
    margin-top: 25px;

    .tooltip-arrow {
      top: -15px;
      left: calc(50% - 30px) !important;
      margin-top: 0;
      margin-bottom: 0;
    }
    .popover-arrow::after {
      top: 2px;
      left: 0;
      border-width: 0 15px 14px;
    }
    .popover-arrow::before {
      top: 1px;
      left: -1px;
      border-width: 0 16px 15px;
    }
  }

  &[x-placement^='right'] {
    margin-left: 25px;

    .tooltip-arrow {
      top: calc(50% - 22px) !important;
      left: -15px;
      margin-right: 0;
      margin-left: 0;
    }
    .popover-arrow::after {
      top: 0;
      left: -7px;
      border-width: 0 15px 14px;
      transform: rotate(-90deg);
    }
    .popover-arrow::before {
      top: 0;
      left: -8px;
      border-width: 0 15px 14px;
      transform: rotate(-90deg);
    }
  }

  &[x-placement^='left'] {
    margin-right: 25px;

    .tooltip-arrow {
      top: calc(50% - 22px) !important;
      right: -15px;
      margin-right: 0;
      margin-left: 0;
    }
    .popover-arrow::after {
      top: 0;
      right: -6px;
      border-width: 0 15px 14px;
      transform: rotate(90deg);
    }
    .popover-arrow::before {
      top: 0;
      right: -7px;
      border-width: 0 15px 14px;
      transform: rotate(90deg);
    }
  }

  &.popover {
    .popover-inner {
      padding: 20px;
      color: #00638a;
      background: #fff;
      border-radius: 15px;
      box-shadow: 0 3px 16px #00000029;
    }
  }

  &[aria-hidden='true'] {
    visibility: hidden;
    opacity: 0;
    transition: opacity 0.15s, visibility 0.15s;
  }

  &[aria-hidden='false'] {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.15s;
  }
}

.VueCarousel-dot:focus {
  outline: none !important;
}

#ismatch,
#isnotmatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  background: #ccc;
  border-radius: 8px;
}

#ismatch {
  background: #61d836;
}

#nprogress .spinner {
  right: 50%;
  left: 50%;
}
.show-password-field {
  @apply pr-4 flex flex-row overflow-hidden p-0 shadow-input border-gray-transparent  border rounded-input bg-white-transparent;
}
.password-input {
  @apply text-blue-darker text-basel font-hairline leading-loose px-5 outline-none w-full;
}
// Design variables and utilities from src/design.
// @import '@design';

// *,
// *::before,
// *::after {
//   box-sizing: border-box;
// }

// body {
//   background: $color-body-bg;
// }
// #app {
//   @extend %typography-small;
// }

// ===
// Base element styles
// ===

// a,
// a:visited {
//   color: $color-link-text;
// }

// h1 {
//   @extend %typography-xxlarge;
// }

// h2 {
//   @extend %typography-xlarge;
// }

// h3 {
//   @extend %typography-large;
// }

// h4 {
//   @extend %typography-medium;
// }

// h5,
// h6 {
//   @extend %typography-small;
// }

// ===
// Vendor
// ===

// #nprogress .bar {
//   background: $color-link-text;
// }
</style>
